import { QueryClient, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { EventData, EventNames, useTracking } from "lib/tracking";
import { filterObject } from "../../../../components/leads/leads_table/utils";
import { graphql } from "../../../generated/app-service-gql";
import {
  LeadQuery,
  LeadsQuery,
  UpdateLeadsMutation,
  UpdateLeadsMutationVariables,
} from "../../../generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";
import { updateLeadQuery, updateSearchLeadsQuery } from "./update_cache/updateLeadEntityCache";
import { LeadDetails } from "./useLead";
import { LeadWorkflow, useLeadWorkflow } from "./useLeadWorkflow";

const keyToLabel: Record<string, string> = {
  name: "Title",
  description: "Description",
  assignedToId: "Assignee",
  stageId: "Stage",
  value: "Value",
  closeDate: "Close date",
  expiryDate: "Expiry date",
};

const UPDATE_LEADS_MUTATION = graphql(`
  mutation updateLeads($updateLeadsRequest: UpdateLeadsRequest!) {
    updateLeads(UpdateLeadsRequest: $updateLeadsRequest) {
      id
      name
      description
      value
      createdAt
      updatedAt
      expiryDate
      assignedTo {
        guid
        firstName
        lastName
        active
      }
      stage {
        id
        name
        color
        iconName
        iconColor
        transitionGroup
      }
    }
  }
`);

type UpdateLeadData = UpdateLeadsMutation["updateLeads"][number];

export function useUpdateLeads(
  options: UseMutationOptions<UpdateLeadsMutation, unknown, UpdateLeadsMutationVariables, unknown>,
) {
  const { logEvent } = useTracking();

  const { data: workflow } = useLeadWorkflow();

  const queryClient = useQueryClient();
  const { data, ...rest } = useGraphQlMutation(UPDATE_LEADS_MUTATION, {
    ...options,
    onSuccess: async (data, variables, context) => {
      // Update any leads in the cache assigned to any of the updated leads
      updateUseLeadsCache(queryClient, data.updateLeads);

      const fieldsThatHaveBeenUpdated = filterObject(variables.updateLeadsRequest.updateData, [
        "name",
        "description",
        "assignedToId",
        "stageId",
        "value",
        "closeDate",
      ]);

      let previousLeads: LeadDetails[] = [];

      const hasUpdatedStage = Object.keys(fieldsThatHaveBeenUpdated).includes("stageId");

      // Only fetch previous lead data if stage has been updated
      if (hasUpdatedStage) {
        previousLeads = getAllPreviousLeads(queryClient, variables.updateLeadsRequest.ids);
      }

      // Send amplitude tracking events for each updated lead and field
      for (const updatedLead of data.updateLeads) {
        for (const [key, newVal] of Object.entries(fieldsThatHaveBeenUpdated)) {
          if (key === "assignedToId" && newVal === null) {
            logEvent(EventNames.leadDetailsUpdated, {
              "Lead ID": updatedLead.id,
              "Field updated": keyToLabel[key],
              "Updated field content": "Unassigned",
            });
          } else if (newVal !== undefined) {
            logEvent(EventNames.leadDetailsUpdated, {
              "Lead ID": updatedLead.id,
              "Field updated": keyToLabel[key],
              "Updated field content": newVal,
            });
          }

          // We want to track if there's been a forward progression or lost stage update
          if (key === "stageId" && !!newVal) {
            const previousLead = previousLeads.find((l) => l.id === updatedLead.id);

            // Shouldn't happen, but failsafe
            if (!previousLead) continue;

            trackLeadStageChange(logEvent, previousLead, workflow, updatedLead);
          }
        }
      }
      // Update cache for all singular lead queries matching the updated leads
      // - crucial that this is at the end because we check previously cached lead data
      // above when tracking stage changes
      updateUseLeadCache(queryClient, data.updateLeads);
      options?.onSuccess?.(data, variables, context);
    },
  });
  return { data, ...rest };
}

/**
 * Tracks lead stage changes to amplitude, including whether the lead was lost or progressed,
 * as well as the previous and new stage names and order, lead ID, value, and title
 * @param logEvent
 * @param previousLead
 * @param workflow
 * @param updatedLead
 */
function trackLeadStageChange(
  logEvent: (eventName: EventNames, eventData?: EventData) => void,
  previousLead: LeadDetails,
  workflow: LeadWorkflow | undefined,
  updatedLead: UpdateLeadData,
) {
  const stageTransitions = workflow?.transitions ?? [];

  // Need to use this method as workflow data from useLeadWorkflow has the order field
  const startLeadTransitionStage = stageTransitions.find(
    (transition) => transition.stage.id === previousLead.stage.id,
  );

  const endLeadTransitionStage = stageTransitions.find(
    (transition) => transition.stage.id === updatedLead.stage.id,
  );

  const eventData: EventData = {
    "Start lead stage name": previousLead.stage.name,
    "End lead stage name": updatedLead.stage.name,
    "Start lead stage order": startLeadTransitionStage?.order,
    "End lead stage order": endLeadTransitionStage?.order,
    "Lead ID": updatedLead.id,
    "Lead value": updatedLead.value,
    "Lead stage group name": updatedLead.stage.transitionGroup,
    "Lead title": updatedLead.name,
  };

  const isForwardProgression =
    (endLeadTransitionStage?.order ?? 0) > (startLeadTransitionStage?.order ?? 0);

  if (endLeadTransitionStage?.group === "Lost") {
    logEvent(EventNames.leadLost, eventData);
  } else if (isForwardProgression) {
    logEvent(EventNames.leadProgressed, eventData);
  }
}

/**
 * Updates any leads in the useLead cache (singular query) assigned to any of the updated leads
 * @param queryClient
 * @param responseData response data from the useUpdateLeads mutation
 */
function updateUseLeadCache(
  queryClient: QueryClient,
  responseData: UpdateLeadsMutation["updateLeads"],
) {
  const updatedLeadIds = responseData.map((l) => l.id);

  const matchingLeadQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["lead"] })
    .filter((q) => {
      const [, queryLeadId] = q.queryKey as [string, string];
      return updatedLeadIds.includes(queryLeadId);
    });

  for (const leadsQuery of matchingLeadQueries) {
    // Getting the leadId from the query key
    const [, leadId] = leadsQuery.queryKey as [string, string];

    updateLeadQuery(queryClient, leadId, (prevData) => {
      const newLeadData = responseData.find((l) => l.id === prevData.id);
      return {
        ...prevData,
        ...newLeadData,
      };
    });
  }
}

/**
 * Updates any leads in the useLeads cache assigned to any of the updated leads.
 * Computes value delta per filter group (queryKey[1]) so that all pages within the
 * same filter get a consistent valueSum update, without affecting unrelated filter caches.
 */
export function updateUseLeadsCache(
  queryClient: QueryClient,
  responseData: UpdateLeadsMutation["updateLeads"],
) {
  const allLeadsQueries = queryClient.getQueryCache().findAll({ queryKey: ["leads"] });
  const updatedLeadIds = responseData.map((l) => l.id);

  // Compute value delta per filter group (queryKey[1] = filter/request params).
  // Each distinct filter has its own valueSum, so we must not apply a delta from
  // one filter group to another.
  const deltaByFilterKey = new Map<string, number>();
  const processedKeys = new Set<string>(); // `${filterKey}:${leadId}` — avoid double-counting

  for (const { queryKey } of allLeadsQueries) {
    const data = queryClient.getQueryData<LeadsQuery>(queryKey);
    if (!data?.leads) continue;
    const filterKey = JSON.stringify(queryKey[1]);
    for (const updatedLead of responseData) {
      if (processedKeys.has(`${filterKey}:${updatedLead.id}`)) continue;
      const cached = data.leads.results.find((l) => l.id === updatedLead.id);
      if (cached !== undefined) {
        processedKeys.add(`${filterKey}:${updatedLead.id}`);
        deltaByFilterKey.set(
          filterKey,
          (deltaByFilterKey.get(filterKey) ?? 0) + (updatedLead.value ?? 0) - (cached.value ?? 0),
        );
      }
    }
  }

  for (const { queryKey } of allLeadsQueries) {
    const filterKey = JSON.stringify(queryKey[1]);
    updateSearchLeadsQuery(
      queryClient,
      queryKey,
      updatedLeadIds,
      (prevData) => {
        const newLeadData = responseData.find((l) => l.id === prevData.id);
        return { ...prevData, ...newLeadData };
      },
      deltaByFilterKey.get(filterKey), // undefined for unrelated filter groups → no override
    );
  }
}

/**
 * Gets all previous lead data from the useLead cache for the given list of updated lead IDs
 * @param queryClient
 * @param updatedLeadIds
 * @returns
 */
function getAllPreviousLeads(queryClient: QueryClient, updatedLeadIds: string[]): LeadDetails[] {
  const matchingLeadQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["lead"] })
    .filter((q) => {
      const [, queryLeadId] = q.queryKey as [string, string];
      return updatedLeadIds.includes(queryLeadId);
    });

  const previousLeads: LeadQuery["lead"][] = [];

  for (const q of matchingLeadQueries) {
    const data = queryClient.getQueryData<LeadQuery>(q.queryKey);
    if (data?.lead) {
      previousLeads.push(data.lead);
    }
  }

  return previousLeads;
}
